if (!customElements.get('sticky-header')) {
  customElements.define(
    'sticky-header',
    class StickyHeader extends HTMLElement {
      connectedCallback() {
        this.header = this.closest('.section-header');
        if (!this.header) return;

        this.headerIsAlwaysSticky =
          this.getAttribute('data-sticky-type') === 'always' ||
          this.getAttribute('data-sticky-type') === 'reduce-logo-size';

        this.luxuryTransparent = this.classList.contains('header-wrapper--luxury-transparent');
        this.headerBounds = {};

        this.setHeaderHeight();

        window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

        if (this.headerIsAlwaysSticky && !this.luxuryTransparent) {
          this.header.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, false);

        this.resizeOverlapHandler = () => {
          if (document.documentElement.classList.contains('template-index-fullpage-snap') && this.luxuryTransparent) {
            this.updateLuxuryChromeOverlap();
          }
          this.setHeaderHeight();
        };
        window.addEventListener('resize', this.resizeOverlapHandler);

        this.createObserver();

        requestAnimationFrame(() => {
          const y = window.pageYOffset || document.documentElement.scrollTop;
          const indexSnap = document.documentElement.classList.contains('template-index-fullpage-snap');
          const editorialHero = Boolean(
            document.querySelector('#MainContent > .shopify-section.aelein-collection-hero:first-child')
          );
          if (this.luxuryTransparent && (indexSnap || this.headerIsAlwaysSticky || editorialHero)) {
            this.updateLuxuryStickyAppearance(y);
          }
        });
      }

      setHeaderHeight() {
        if (!this.header) return;
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      }

      disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
        if (this.resizeOverlapHandler) {
          window.removeEventListener('resize', this.resizeOverlapHandler);
        }
      }

      createObserver() {
        if (!this.header) return;
        const observer = new IntersectionObserver((entries, obs) => {
          this.headerBounds = entries[0].intersectionRect;
          obs.disconnect();
        });
        observer.observe(this.header);
      }

      updateLuxuryStickyAppearance(scrollTop) {
        const indexPage = document.documentElement.classList.contains('template-index-fullpage-snap');
        /* 首页：不靠白条切换；顶栏始终透明，字色由 updateLuxuryChromeOverlap / CSS color-mix */
        if (indexPage && this.luxuryTransparent) {
          this.header.classList.remove('shopify-section-header-sticky');
          this.updateLuxuryChromeOverlap();
          return;
        }

        const editorialHero = document.querySelector('#MainContent > .shopify-section.aelein-collection-hero:first-child');
        if (editorialHero && this.luxuryTransparent) {
          const heroTop = editorialHero.getBoundingClientRect().top + scrollTop;
          const heroBottom = heroTop + editorialHero.offsetHeight;
          const buffer = 56;
          if (scrollTop + buffer < heroBottom) {
            this.header.classList.remove('shopify-section-header-sticky');
          } else {
            this.header.classList.add('shopify-section-header-sticky');
          }
          return;
        }

        const threshold = 8;
        const transparentHeroChrome = indexPage && scrollTop <= threshold;
        if (transparentHeroChrome) {
          this.header.classList.remove('shopify-section-header-sticky');
        } else {
          this.header.classList.add('shopify-section-header-sticky');
        }
      }

      /**
       * 首页全屏分屏：仅「当前所在屏」索引为 0（第一屏 hero）时浅色导航——overlap=0；
       * 第 2、3、4…屏及 footer 一律 overlap=1（黑字黑 Logo）。顺序与 fullpage-snap 一致。
       */
      updateLuxuryChromeOverlap() {
        if (!document.documentElement.classList.contains('template-index-fullpage-snap') || !this.luxuryTransparent) return;
        if (this.classList.contains('header-wrapper--nav-disclosure-open')) return;

        const sectionSelector = '#MainContent > .shopify-section';
        const footerSelector = 'body > .shopify-section-group-footer-group';
        const sections = Array.from(document.querySelectorAll(sectionSelector));
        const footerGroup = document.querySelector(footerSelector);
        const targets = sections.filter((el) => el && el.getBoundingClientRect().height > 0);
        if (footerGroup && footerGroup.getBoundingClientRect().height > 0) {
          targets.push(footerGroup);
        }

        if (!targets.length) {
          this.header.classList.remove('luxury-header-after-hero');
          document.documentElement.style.setProperty('--luxury-chrome-overlap', '0');
          return;
        }

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const probe = scrollTop + window.innerHeight * 0.35;

        let index = 0;
        for (let i = 0; i < targets.length; i += 1) {
          const docTop = targets[i].getBoundingClientRect().top + scrollTop;
          if (docTop <= probe) index = i;
        }

        const overlap = index === 0 ? 0 : 1;
        this.header.classList.toggle('luxury-header-after-hero', overlap === 1);
        document.documentElement.style.setProperty('--luxury-chrome-overlap', String(overlap));
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        const indexSnap = document.documentElement.classList.contains('template-index-fullpage-snap');
        const editorialHero = Boolean(
          document.querySelector('#MainContent > .shopify-section.aelein-collection-hero:first-child')
        );
        if (this.luxuryTransparent && (indexSnap || this.headerIsAlwaysSticky || editorialHero)) {
          this.updateLuxuryStickyAppearance(scrollTop);
        }

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (this.preventHide) return;
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventReveal) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      }

      closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
        this.disclosures.forEach((disclosure) => disclosure.close());
        const unifiedMega = this.querySelector('mega-menu-unified-controller');
        if (unifiedMega && typeof unifiedMega.close === 'function') {
          unifiedMega.close();
        }
      }

      closeSearchModal() {
        this.searchModal = this.searchModal || this.header.querySelector('details-modal');
        if (this.searchModal && typeof this.searchModal.close === 'function') {
          this.searchModal.close(false);
        }
      }
    }
  );
}
